import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { SearchFieldDialogAction } from '@/components/custom/erp/search-field-v2/types';
import { buildQuickCreateDialogActions } from '@/lib/quick-create-dialog-actions';
import type { FormMode } from '@/types/form';

export interface QuickCreateRenderProps<TData = any, TContext = unknown> {
  context?: TContext;
  initialData?: TData;
  mode: Extract<FormMode, 'add' | 'edit'>;
  onClose: () => void;
  onSaved: (item: TData) => void;
  open: boolean;
}

export interface QuickCreateAdapter<TData = any, TContext = unknown> {
  render: (props: QuickCreateRenderProps<TData, TContext>) => ReactNode;
}

export interface OpenQuickCreateOptions<TData = any, TContext = unknown> {
  context?: TContext;
  initialData?: TData;
  key: string;
  mode?: Extract<FormMode, 'add' | 'edit'>;
  onSaved?: (item: TData) => void;
}

interface ActiveQuickCreate<TData = any, TContext = unknown> extends OpenQuickCreateOptions<TData, TContext> {
  mode: Extract<FormMode, 'add' | 'edit'>;
}

interface QuickCreateContextValue {
  getRefreshKey: (key: string) => number;
  openQuickCreate: <TData = any, TContext = unknown>(options: OpenQuickCreateOptions<TData, TContext>) => void;
}

interface QuickCreateProviderProps {
  adapters: Record<string, QuickCreateAdapter>;
  children: ReactNode;
}

const QuickCreateContext = createContext<QuickCreateContextValue | null>(null);

export function QuickCreateProvider({ adapters, children }: QuickCreateProviderProps) {
  const [activeQuickCreates, setActiveQuickCreates] = useState<ActiveQuickCreate[]>([]);
  const [refreshKeys, setRefreshKeys] = useState<Record<string, number>>({});

  const closeQuickCreate = useCallback(() => {
    setActiveQuickCreates(prev => prev.slice(0, -1));
  }, []);

  const openQuickCreate = useCallback(
    <TData, TContext>({ mode = 'add', ...options }: OpenQuickCreateOptions<TData, TContext>) => {
      if (!adapters[options.key]) {
        return;
      }

      setActiveQuickCreates(prev => [
        ...prev,
        {
          ...options,
          mode
        }
      ]);
    },
    [adapters]
  );

  const getRefreshKey = useCallback((key: string) => refreshKeys[key] ?? 0, [refreshKeys]);

  const contextValue = useMemo<QuickCreateContextValue>(
    () => ({
      getRefreshKey,
      openQuickCreate
    }),
    [getRefreshKey, openQuickCreate]
  );

  return (
    <QuickCreateContext.Provider value={contextValue}>
      {children}
      {activeQuickCreates.map((active, index) => {
        const adapter = adapters[active.key];
        if (!adapter) return null;

        return (
          <React.Fragment key={`${active.key}-${index}`}>
            {adapter.render({
              context: active.context,
              initialData: active.initialData,
              mode: active.mode,
              onClose: closeQuickCreate,
              onSaved: item => {
                setRefreshKeys(currentValue => ({
                  ...currentValue,
                  [active.key]: (currentValue[active.key] ?? 0) + 1
                }));
                active.onSaved?.(item);
                closeQuickCreate();
              },
              open: true
            })}
          </React.Fragment>
        );
      })}
    </QuickCreateContext.Provider>
  );
}

export const useQuickCreate = () => useContext(QuickCreateContext);

interface UseQuickCreateDialogActionsOptions<TData = any, TContext = unknown> {
  allowEdit?: boolean;
  closeLookupOnOpen?: boolean;
  createIcon?: number;
  createTitle?: string;
  editIcon?: number;
  editTitle?: string;
  initialData?: TData | null;
  key: string;
  context?: TContext;
  onSaved?: (item: TData) => void;
}

export const useQuickCreateDialogActions = <TData, TContext = unknown>({
  allowEdit = true,
  closeLookupOnOpen = false,
  createIcon,
  createTitle,
  editIcon,
  editTitle,
  initialData,
  key,
  context,
  onSaved
}: UseQuickCreateDialogActionsOptions<TData, TContext>) => {
  const quickCreate = useQuickCreate();

  return useMemo<SearchFieldDialogAction<TData>[]>(
    () =>
      quickCreate
        ? buildQuickCreateDialogActions<TData>({
            allowEdit,
            createIcon,
            createTitle,
            editIcon,
            editTitle,
            initialData,
            onCreate: actionContext => {
              if (closeLookupOnOpen) {
                actionContext.closeDialog();
              }

              quickCreate.openQuickCreate<TData, TContext>({
                context,
                key,
                mode: 'add',
                onSaved
              });
            },
            onEdit: (item, actionContext) => {
              if (closeLookupOnOpen) {
                actionContext.closeDialog();
              }

              quickCreate.openQuickCreate<TData, TContext>({
                context,
                initialData: item,
                key,
                mode: 'edit',
                onSaved
              });
            }
          })
        : [],
    [
      allowEdit,
      closeLookupOnOpen,
      context,
      createIcon,
      createTitle,
      editIcon,
      editTitle,
      initialData,
      key,
      onSaved,
      quickCreate
    ]
  );
};
